<template>
  <CollapsibleRoot
    v-model:open="open"
    :defaultOpen="defaultOpen"
    :class="['Collapsible', { 'Collapsible--compact': compact }]"
  >
    <CollapsibleTrigger asChild>
      <div class="Collapsible-header">
        <slot name="header">
          <div>
            <h3 v-if="title" class="Collapsible-title">
              {{ title }}
            </h3>
            <h4 v-if="subtitle" class="Collapsible-subtitle">
              {{ subtitle }}
            </h4>
            <p
              v-if="description"
              class="Collapsible-description"
              v-html="description"
            />
          </div>
        </slot>
        <IconButton
          :icon="open ? 'chevron-up' : 'chevron-down'"
          size="lg"
          class="Collapsible-toggleButton"
          :ariaLabel="t('ui.components.accordion.toggleAriaLabel')"
        />
      </div>
    </CollapsibleTrigger>

    <CollapsibleContent class="Collapsible-contentWrapper">
      <hr class="Collapsible-divider">
      <slot name="content">
        <p v-if="content" class="Collapsible-content" v-html="content" />
      </slot>
    </CollapsibleContent>
  </CollapsibleRoot>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from 'reka-ui';
import IconButton from '@cvo/ui/components/atoms/IconButton/IconButton.vue';
import type { CollapsibleProps } from './Collapsible.model';

const props = withDefaults(defineProps<CollapsibleProps>(), {
  defaultOpen: false,
});

const { t } = useI18n();

// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const open = ref(props.defaultOpen);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@keyframes collapsibleSlideDown {
  from { height: 0; }
  to { height: var(--reka-collapsible-content-height); }
}

@keyframes collapsibleSlideUp {
  from { height: var(--reka-collapsible-content-height); }
  to { height: 0; }
}

@layer cvo-ui {
  .Collapsible {
    padding: vars.$dimensions-d-4;
    border-radius: vars.$molecules-accordion-lg-md-border-radius;
    border: vars.$molecules-accordion-border-stroke solid vars.$molecules-accordion-border-color;
    background-color: vars.$molecules-accordion-bg-color;

    @include responsive.mobile {
      border-radius: vars.$molecules-accordion-sm-border-radius;
    }

    &-contentWrapper {
      overflow: hidden;

      &[data-state='open'] {
        animation: collapsibleSlideDown vars.$transition-base ease-out;
      }

      &[data-state='closed'] {
        animation: collapsibleSlideUp vars.$transition-base ease-in;
      }
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      width: 100%;
    }

    &-title {
      color: vars.$molecules-accordion-title-color;
      @include text.text('lg');
      font-weight: vars.$typography-weight-strong;
    }

    &-subtitle {
      color: vars.$molecules-accordion-subtitle-color;
      @include text.text('sm');
      font-weight: vars.$typography-weight-strong;
    }

    &-description {
      color: vars.$molecules-accordion-description-color;
      @include text.text('xs');
    }

    &-content {
      color: vars.$molecules-accordion-contentaccordion-text-color;
      @include text.text('sm');
    }

    &-divider {
      margin: vars.$dimensions-d-3 0;
      border: none;
      border-top: vars.$molecules-accordion-border-stroke solid vars.$molecules-accordion-border-color;
    }

    &--compact {
      padding: 0;
      border: none;
    }
  }
}
</style>
